import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  Divider,
  Grid,
  Switch,
  FormControlLabel,
  Paper,
  Avatar,
  IconButton,
  InputAdornment,
  CircularProgress,
} from '@mui/material';
import {
  Security,
  Person,
  VpnKey,
  Email,
  Phone,
  Visibility,
  VisibilityOff,
  Save,
  Cancel,
  AdminPanelSettings,
} from '@mui/icons-material';
import { useAdminAuth } from '../context/AdminAuthContext';
import MFASettings from './MFASettings';

interface AdminSettingsProps {
  onClose: () => void;
}

const AdminSettings: React.FC<AdminSettingsProps> = ({ onClose }) => {
  const { admin, logActivity, changePassword, updateProfile } = useAdminAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  // Profile settings
  const [profileData, setProfileData] = useState({
    name: admin?.name || '',
    email: admin?.email || '',
    phone: admin?.phone || '',
  });

  // Password change
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });

  // Security settings
  const [securitySettings, setSecuritySettings] = useState({
    mfaEnabled: true,
    sessionTimeout: 30,
    loginNotifications: true,
    ipWhitelist: ['*'],
  });

  const handleProfileUpdate = async () => {
    setIsLoading(true);
    setSuccessMessage('');
    setErrorMessage('');

    try {
      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(profileData.email)) {
        throw new Error('Please enter a valid email address');
      }

      // Validate phone format if provided
      if (profileData.phone && profileData.phone.trim()) {
        const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
        if (!phoneRegex.test(profileData.phone.replace(/[-\s()]/g, ''))) {
          throw new Error('Please enter a valid phone number');
        }
      }

      // Use the updateProfile function from context
      await updateProfile(profileData);
      setSuccessMessage('Profile updated successfully!');
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Failed to update profile');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordChange = async () => {
    setIsLoading(true);
    setSuccessMessage('');
    setErrorMessage('');

    try {
      // Validate passwords
      if (passwordData.newPassword.length < 8) {
        throw new Error('New password must be at least 8 characters long');
      }

      if (passwordData.newPassword !== passwordData.confirmPassword) {
        throw new Error('New passwords do not match');
      }

      // Use the secure backend API to change password
      await changePassword(passwordData.currentPassword, passwordData.newPassword);
      
      setSuccessMessage('Password updated successfully!');
      
      // Clear password fields
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Failed to change password');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSecuritySettingsUpdate = async () => {
    setIsLoading(true);
    setSuccessMessage('');
    setErrorMessage('');

    try {
      // In development mode, just log the activity
      if (process.env.NODE_ENV === 'development' || process.env.REACT_APP_DEV_MODE === 'true') {
        console.log('🔒 Security settings updated:', securitySettings);
        setSuccessMessage('Security settings updated successfully!');
        await logActivity('update_settings', 'security');
      } else {
        // Production API call would go here
        const token = localStorage.getItem('admin_token');
        const response = await fetch('/api/admin/security-settings', {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify(securitySettings),
        });

        if (!response.ok) {
          throw new Error('Failed to update security settings');
        }

        setSuccessMessage('Security settings updated successfully!');
        await logActivity('update_settings', 'security');
      }
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Failed to update security settings');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 800, mx: 'auto' }}>
      {/* Header */}
      <Box display="flex" alignItems="center" mb={3}>
        <AdminPanelSettings sx={{ fontSize: 32, mr: 2, color: 'primary.main' }} />
        <Box>
          <Typography variant="h4" fontWeight="bold">
            Admin Settings
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage your account and security preferences
          </Typography>
        </Box>
      </Box>

      {/* Success/Error Messages */}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccessMessage('')}>
          {successMessage}
        </Alert>
      )}
      {errorMessage && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setErrorMessage('')}>
          {errorMessage}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Profile Settings */}
        <Grid item xs={12} md={6}>
          <Card elevation={2}>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <Person sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" fontWeight="bold">
                  Profile Information
                </Typography>
              </Box>

              <TextField
                fullWidth
                label="Full Name"
                value={profileData.name}
                onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                margin="normal"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Person color="action" />
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                fullWidth
                label="Email Address"
                type="email"
                value={profileData.email}
                onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                margin="normal"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Email color="action" />
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                fullWidth
                label="Phone Number"
                value={profileData.phone}
                onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                margin="normal"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Phone color="action" />
                    </InputAdornment>
                  ),
                }}
              />

              <Button
                variant="contained"
                startIcon={<Save />}
                onClick={handleProfileUpdate}
                disabled={isLoading}
                sx={{ mt: 2 }}
                fullWidth
              >
                {isLoading ? <CircularProgress size={20} /> : 'Update Profile'}
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Password Change */}
        <Grid item xs={12} md={6}>
          <Card elevation={2}>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <VpnKey sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" fontWeight="bold">
                  Change Password
                </Typography>
              </Box>

              <TextField
                fullWidth
                label="Current Password"
                type={showPasswords.current ? 'text' : 'password'}
                value={passwordData.currentPassword}
                onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                margin="normal"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPasswords({ ...showPasswords, current: !showPasswords.current })}
                        edge="end"
                      >
                        {showPasswords.current ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                fullWidth
                label="New Password"
                type={showPasswords.new ? 'text' : 'password'}
                value={passwordData.newPassword}
                onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                margin="normal"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPasswords({ ...showPasswords, new: !showPasswords.new })}
                        edge="end"
                      >
                        {showPasswords.new ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                fullWidth
                label="Confirm New Password"
                type={showPasswords.confirm ? 'text' : 'password'}
                value={passwordData.confirmPassword}
                onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                margin="normal"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPasswords({ ...showPasswords, confirm: !showPasswords.confirm })}
                        edge="end"
                      >
                        {showPasswords.confirm ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Button
                variant="contained"
                startIcon={<Save />}
                onClick={handlePasswordChange}
                disabled={isLoading || !passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword}
                sx={{ mt: 2 }}
                fullWidth
              >
                {isLoading ? <CircularProgress size={20} /> : 'Change Password'}
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Multi-Factor Authentication */}
        <Grid item xs={12}>
          <MFASettings />
        </Grid>

        {/* Security Settings */}
        <Grid item xs={12}>
          <Card elevation={2}>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <Security sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" fontWeight="bold">
                  Security Settings
                </Typography>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={securitySettings.mfaEnabled}
                        onChange={(e) => setSecuritySettings({ ...securitySettings, mfaEnabled: e.target.checked })}
                      />
                    }
                    label="Multi-Factor Authentication"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={securitySettings.loginNotifications}
                        onChange={(e) => setSecuritySettings({ ...securitySettings, loginNotifications: e.target.checked })}
                      />
                    }
                    label="Login Notifications"
                  />
                </Grid>
              </Grid>

              <TextField
                fullWidth
                label="Session Timeout (minutes)"
                type="number"
                value={securitySettings.sessionTimeout}
                onChange={(e) => setSecuritySettings({ ...securitySettings, sessionTimeout: parseInt(e.target.value) || 30 })}
                margin="normal"
                inputProps={{ min: 5, max: 480 }}
              />

              <Button
                variant="contained"
                startIcon={<Save />}
                onClick={handleSecuritySettingsUpdate}
                disabled={isLoading}
                sx={{ mt: 2 }}
              >
                {isLoading ? <CircularProgress size={20} /> : 'Update Security Settings'}
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Action Buttons */}
      <Box display="flex" justifyContent="flex-end" mt={3} gap={2}>
        <Button
          variant="outlined"
          startIcon={<Cancel />}
          onClick={onClose}
        >
          Close
        </Button>
      </Box>

      {/* Development Notice */}
      {(process.env.NODE_ENV === 'development' || process.env.REACT_APP_DEV_MODE === 'true') && (
        <Paper sx={{ mt: 3, p: 2, backgroundColor: 'rgba(255, 193, 7, 0.1)' }}>
          <Typography variant="body2" color="warning.main" textAlign="center">
            ⚠️ Development Mode - Changes are stored locally. In production, these would be saved to a secure backend.
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default AdminSettings; 